import { Component, NgZone, OnDestroy, OnInit, AfterViewInit, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as Handsontable from 'handsontable';
import { HotTableRegisterer } from '@handsontable/angular';

import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';

import { CoverUploadComponent } from '../../components/coverupload/coverUpload.component';
import { WaveformComponent } from '../../components/waveform/waveform.component';
import { CoverSearchComponent } from '../../components/coversearch/coverSearch.component';
import { TmdbSearchComponent } from '../../components/tmdbsearch/tmdbSearch.component';
import { ArtistPictureSearch } from '../../components/artistpicturesearch/artistPictureSearch.component';
import { ArtistInstagramSearch } from '../../components/artistinstagramsearch/artistInstagramSearch.component';
import { ChoiceWinmediaComponent } from '../../components/choicewinmedia/choiceWinmedia.component';
import { MetadataReviewComponent } from '../../components/metadatareview/metadataReview.component';

import { Musiques } from '../../ressources/types/musiques';
import { WinmediaData } from '../../ressources/types/winmediaData';
import { MetadataSearch } from '../../ressources/types/metadataSearch';
import {
  MetadataProposal, MetadataProposalResult, ReleaseDateValue, GenreValue, DiscTrackNumberValue, NationalityValue,
  MusicbrainzIdsValue, SpotifyCandidateSummary, METADATA_PROPOSAL_SILENT_FIELDS, METADATA_PROPOSAL_REVIEWABLE_FIELDS
} from '../../ressources/types/metadataProposal';

import { CommonFunctionsService } from '../../providers/Global/commonFunctions.service';
import { AuthentificationService } from '../../providers/Global/authentification.service';
import { AnalyzerService } from '../../providers/Table/analyzer.service';
import { MusicsDataService } from '../../providers/Table/musicsData.service';
import { DataPassService } from '../../providers/Global/dataPass.service';
import { MysqlSyncService } from '../../providers/Global/mysqlSync.service';

import { ChosenEditor } from './chosenEditor';

import Controls from './Controls.js';

import { COL_HEADERS } from '../../ressources/constants/colHeaders';

/**
 * Icône discrète ajoutée dans une cellule, visible seulement au survol (comme le fait Handsontable
 * lui-même pour la flèche de son AutocompleteRenderer natif : élément toujours présent, juste
 * quasi-invisible jusqu'au survol). Évite de dépendre d'une règle CSS ":hover" dans le stylesheet du
 * composant : les renderers Handsontable créent leurs éléments en DOM pur, hors du template Angular,
 * donc l'encapsulation de vue (attributs _ngcontent) ne s'appliquerait pas à un sélecteur scoping normal.
 */
interface SearchIconOptions {
  // Icône toujours visible plutôt que révélée au survol (ex: icône "ligne prête" dans la gouttière).
  alwaysVisible?: boolean;
  // Centrée (horizontalement + verticalement) dans la cellule plutôt que collée en haut à droite
  // (utilisé pour la gouttière de gauche, plus étroite qu'une cellule de donnée classique).
  centered?: boolean;
}

function appendSearchIcon(td: HTMLElement, onClick: () => void, title: string, symbol = '🔍', options: SearchIconOptions = {}) {
  const existing = td.querySelector('.htActionIcon');
  if (existing) {
    existing.remove();
  }

  const icon = document.createElement('span');
  icon.className = 'htActionIcon';
  icon.textContent = symbol;
  icon.title = title;
  icon.style.display = 'inline-block';
  icon.style.width = '14px';
  icon.style.height = '14px';
  icon.style.fontSize = '12px';
  icon.style.lineHeight = '14px';
  icon.style.textAlign = 'center';
  icon.style.overflow = 'hidden';
  icon.style.cursor = 'pointer';
  icon.style.zIndex = '5';

  // Positionnement en absolu dans tous les cas (jamais de display:flex sur td) : td est un vrai <td>/<th>
  // que Handsontable gère lui-même pour le dimensionnement des lignes/colonnes — changer son display
  // casse ce calcul (vu en test : gouttière de ligne qui gonfle démesurément).
  td.style.position = 'relative';
  icon.style.position = 'absolute';
  if (options.centered) {
    icon.style.top = '50%';
    icon.style.left = '50%';
    icon.style.transform = 'translate(-50%, -50%)';
  } else {
    icon.style.top = '2px';
    icon.style.right = '2px';
  }

  if (options.alwaysVisible) {
    icon.style.opacity = '1';
  } else {
    icon.style.opacity = '0';
    icon.style.transition = 'opacity 0.15s';
    Handsontable.default.dom.addEvent(td, 'mouseenter', function() {
      icon.style.opacity = '1';
    });
    Handsontable.default.dom.addEvent(td, 'mouseleave', function() {
      icon.style.opacity = '0';
    });
  }

  Handsontable.default.dom.addEvent(icon, 'mousedown', function(event) {
    event?.preventDefault();
    event?.stopPropagation();
  });
  Handsontable.default.dom.addEvent(icon, 'click', function(event) {
    event?.preventDefault();
    event?.stopPropagation();
    onClick();
  });

  td.appendChild(icon);
}

/**
 * Préfixe le libellé d'un item de menu contextuel avec le même symbole que l'icône de survol posée
 * dans la cellule (voir appendSearchIcon), pour visualiser que c'est la même action à deux endroits.
 * Repeuple le wrapper fourni par Handsontable (plutôt que de renvoyer un élément séparé) pour garder
 * ses marges par défaut (.htItemWrapper) — comportement identique au rendu par défaut, juste préfixé.
 */
function menuItemIconRenderer(symbol: string) {
  return (hot, wrapper, row, col, prop, itemValue) => {
    wrapper.innerHTML = symbol + ' ' + itemValue;
    // Handsontable ajoute la classe .htCustomMenuRenderer dès qu'un item a un renderer custom, et son
    // CSS impose "cursor: auto" sur le TD (pensé pour des renderers non cliquables) : sans ce style
    // inline (prioritaire sur la feuille de style externe), le curseur ne passe plus en pointeur au
    // survol de l'item alors qu'il reste bien cliquable.
    wrapper.style.cursor = 'pointer';
    return wrapper;
  };
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit, OnDestroy, AfterViewInit {

  intervalSync;

  needSync = true;

  selectedWaveformSub;
  statusWaveformSub;

  selectedWaveform = 9999;

  dataFiles: Musiques[] = [];

  winmediaData: WinmediaData;

  countriesJSON;

  countries;

  proprietes = [];

  genresAssociation = [];

  stationWithGenresAssociation = [];

  colHeaders = COL_HEADERS; // En-têtes de colonnes dans colHeaders.ts

  columns;

  dataVEF = ['Française', 'Etrangère'];
  dataSexe = ['Interprète féminin', 'Interprète masculin', 'Mixte', 'Aucune', 'Inconnu/Autre'];
  dataDiffusion = ['Toujours', 'Jamais', 'Hors matinale', 'Soirée', 'Nuit (18h-7h)', 'Nuit (21h-6h)', 'Hors journée sauf Week-end', 'Hors journée sauf Dimanche', 'Samedi Dimanche Matin (7h-12h)'];

  defaultFade = 3;

  contextMenu = {
    items: {
      'info': {
        renderer: function (hot, wrapper, row, col, prop, itemValue) {
          const elem = document.createElement('marquee');
          elem.style.cssText = 'background: lightgray;';
          elem.textContent = 'Options avancées';
          return elem;
        },
        disableSelection: true,
        isCommand: false
      },
      /*'remove_row': {
        // RAJOUTER RETRAIT MIX + INACTIF SI EN COURS D'ANALYSE
        name: 'Retirer ligne(s) sélectionnée(s) [désactivé]',
        disableSelection: true, // desactivation temporaire
        isCommand: false // desactivation temporaire
      },*/
      'open_image': {
        name: 'Ouvrir l\'image',
        hidden: function () {
          return this.getColHeader(this.getSelectedLast()[1]) !== 'Cover';
        },
        callback: (key, selection, clickEvent) => {
          let cover = this.dataFiles[selection[0].start.row].music.cover.trim();
          if (cover.indexOf('http') === 0) {
            console.log(cover);
            window.open(cover, '_blank');
          }
        }
      },
      'import_cover': {
        name: 'Importer une image',
        hidden: function () {
          return this.getColHeader(this.getSelectedLast()[1]) !== 'Cover';
        },
        callback: (key, selection, clickEvent) => {
          this.zone.run(() => {
            this.addCover(selection[0].start.row, selection[0].end.row, 'cover');
          });
        }
      },
      'import_artist_picture': {
        name: 'Importer une image de l\'artiste',
        hidden: function () {
          return this.getColHeader(this.getSelectedLast()[1]) !== 'Photo artiste';
        },
        callback: (key, selection, clickEvent) => {
          this.zone.run(() => {
            this.addCover(selection[0].start.row, selection[0].end.row, 'artist_picture');
          });
        }
      },
      'discogs': {
        name: 'Rechercher une image sur Discogs (+ année et album)',
        renderer: menuItemIconRenderer('🔍'),
        hidden: function () {
          return this.getColHeader(this.getSelectedLast()[1]) !== 'Cover';
        },
        callback: (key, selection, clickEvent) => {
          this.zone.run(() => {
            this.searchDiscogsCover(selection[0].start.row, selection[0].end.row);
          });
        }
      },
      'tmdb': {
        name: 'Rechercher une image sur TMDB (+ année)',
        hidden: function () {
          return this.getColHeader(this.getSelectedLast()[1]) !== 'Cover';
        },
        callback: (key, selection, clickEvent) => {
          this.zone.run(() => {
            this.searchTmdbCover(selection[0].start.row, selection[0].end.row);
          });
        }
      },
      'artist_picture': {
        name: 'Rechercher une image de l\'artiste',
        renderer: menuItemIconRenderer('🔍'),
        hidden: function () {
          return this.getColHeader(this.getSelectedLast()[1]) !== 'Photo artiste';
        },
        callback: (key, selection, clickEvent) => {
          this.zone.run(() => {
            this.searchArtistPicture(selection[0].start.row, selection[0].end.row);
          });
        }
      },
      'artist_instagram': {
        name: 'Rechercher Instagram (MusicBrainz + Genius)',
        renderer: menuItemIconRenderer('🔍'),
        hidden: function () {
          return this.getColHeader(this.getSelectedLast()[1]) !== 'Instagram';
        },
        callback: (key, selection, clickEvent) => {
          this.zone.run(() => {
            this.searchArtistInstagram(selection[0].start.row, selection[0].end.row);
          });
        }
      },
      // Pas de ":" dans cette clé : Handsontable découpe les clés de menu sur ":" pour son propre
      // système de sous-commandes (ex: l'ancien 'reload:metadata' sous-entendait une commande parente
      // 'reload'). Comme ce menu est maintenant un item de premier niveau et non plus un sous-menu,
      // un ":" ferait planter executeCommand avec "Menu command 'reload' not exists.".
      'reload_metadata': {
        name: 'Analyse metadonnées (titre, artiste, album)',
        renderer: menuItemIconRenderer('🔄'),
        // Réservé à une seule ligne : la fenêtre de review des propositions qui accompagne
        // cette analyse ne gère pas (encore) plusieurs fichiers à la fois.
        // "Analyse fichier son" (ré-identification par empreinte audio ACRCloud) a été retirée : sur un
        // fichier déjà importé, l'empreinte ne change pas, donc la retenter redonne quasi toujours le
        // même résultat qu'à l'import — cas d'usage trop rare pour justifier un second flux de review.
        disabled: function () {
          const selected = this.getSelectedLast();
          return !selected || selected[0] !== selected[2];
        },
        callback: (key, selection, clickEvent) => {
          setTimeout(() => {
            // Handsontable exécute ses callbacks hors de la zone Angular : sans ce zone.run,
            // la modale (composant Angular avec liaison de données) se crée bien mais Angular
            // ne détecte aucun changement tant qu'une opération async "zone-aware" (ex: une
            // requête HTTP) ne survient — d'où le spinner invisible jusqu'à la fin de la recherche.
            this.zone.run(() => {
              this.reloadMetadataWithReview(selection[0].start.row);
            });
          }, 0);
        }
      }
    }
  };

  private hotRegister = new HotTableRegisterer();
  hotId = 'hotInstance';

  hotSettings: Handsontable.default.GridSettings;

  /* Copy/Paste data stock */
  cp_row;
  cp_column;
  cp_data;
  cp_validate;

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    /* Custom Copy/Paste pour colonnes Pays/Stations/Propriétés */
    if (this.cp_column === 11 || this.cp_column === 13 || this.cp_column === 14) {
      /* Copy */
      if (event.keyCode === 67 && event.ctrlKey) {
        this.cp_data = this.hotRegister.getInstance(this.hotId).getDataAtCell(this.cp_row, this.cp_column);
        this.cp_validate = this.cp_column;
      }
      /* Paste */
      if (event.keyCode === 86 && event.ctrlKey) {
        if (this.cp_validate === this.cp_column) {
          this.hotRegister.getInstance(this.hotId).setDataAtCell(this.cp_row, this.cp_column, this.cp_data);
        }
      }
    }
  }

  constructor(private activatedRoute: ActivatedRoute,
              private authentificationService: AuthentificationService,
              private analyzerService: AnalyzerService,
              private musicsDataService: MusicsDataService,
              private dataPassService: DataPassService,
              private mysqlSyncService: MysqlSyncService,
              private matDialog: MatDialog,
              private bottomSheet: MatBottomSheet,
              private snackBar: MatSnackBar,
              private zone: NgZone,
              public CF: CommonFunctionsService) {}

  ngOnInit() {
    this.dataFiles = [];
    this.needSync = true;
    this.startInterval();
    const files = this.dataPassService.import_files;
    const configTable = this.dataPassService.import_configTable;
    this.proprietes = this.dataPassService.import_proprietes;
    this.genresAssociation = this.dataPassService.import_genres_association;
    this.stationWithGenresAssociation = this.dataPassService.import_station_with_genres_association;
    this.winmediaData = this.dataPassService.import_winmediaData;
    this.countriesJSON = this.dataPassService.import_countriesJSON;
    this.countries = this.dataPassService.import_countries;
    this.statusWaveformSub = this.dataPassService.statusWaveform.subscribe(message => {
      let i = 0;
      for (const mess of message) {
        files[i].statusWaveform = mess.status;
        if (this.dataFiles.length > 0) {
          this.dataFiles[i].status.waveformGenerated = mess.status;
        }
        i++;
      }
      if (this.dataFiles.length > 0) {
        this.hotRegister.getInstance(this.hotId).render();
      }
    });
    this.columns = [
      {
        data: 'status.general',
        width: 20,
        readOnly: true,
        // renderer: this.statusRender,
        renderer: (instance, td, row, col, prop, value, cellProperties) =>
          this.statusRender(instance, td, row, col, prop, value, cellProperties, this.dataFiles),
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.cover',
        width: 25,
        renderer: (instance, td, row, col, prop, value, cellProperties) =>
          this.coverRender(instance, td, row, col, prop, value, cellProperties,
            () => this.zone.run(() => this.searchDiscogsCover(row, row)), 'Rechercher sur Discogs'),
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.title',
        width: 55,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        data: 'music.performer',
        width: 55,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        data: 'music.album',
        width: 55,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        data: 'music.version',
        width: 35,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.year',
        width: 30,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        data: 'music.release_date',
        width: 30,
        className: 'htMiddle htCenter',
        type: 'date',
        dateFormat: 'YYYY-MM-DD',
        correctFormat: true,
        datePickerConfig: {
          yearRange: [1960,(new Date()).getFullYear()],
          firstDay: 1
        },
        renderer: (instance, td, row, col, prop, value, cellProperties) =>
          this.releaseDateEmptyRenderer(instance, td, row, col, prop, value, cellProperties, this.dataFiles, this.proprietes)
      },
      {
        data: 'music.instagram',
        width: 25,
        className: 'htMiddle htCenter',
        renderer: (instance, td, row, col, prop, value, cellProperties) =>
          this.instagramRenderer(instance, td, row, col, prop, value, cellProperties,
            () => this.zone.run(() => this.searchArtistInstagram(row, row)), 'Rechercher Instagram')
      },
      {
        data: 'music.sexe',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.dataSexe,
        allowInvalid: false,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        data: 'music.flag',
        editor: ChosenEditor,
        width: 30,
        chosenOptions: {
          multiple: false,
          data: this.countries
        },
        className: 'htMiddle htCenter',
        renderer: (instance, td, row, col, prop, value, cellProperties) =>
          this.flagRenderer(instance, td, row, col, prop, value, cellProperties, this.countriesJSON)
      },
      {
        data: 'music.vef',
        width: 25,
        type: 'dropdown',
        trimDropdown: false,
        source: this.dataVEF,
        allowInvalid: false,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        renderer: this.customDropdownRenderer,
        editor: ChosenEditor,
        width: 35,
        data: 'music.stations',
        chosenOptions: {
          multiple: true,
          data: this.winmediaData.stations
        },
        className: 'htMiddle htCenter'
      },
      {
        renderer: this.customDropdownRenderer,
        editor: ChosenEditor,
        width: 35,
        data: 'music.proprietes',
        chosenOptions: {
          multiple: true,
          data: this.proprietes
        },
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.diffusion',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.dataDiffusion,
        allowInvalid: false,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        data: 'music.genre1',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.winmediaData.genre,
        allowInvalid: false,
        className: 'htMiddle htCenter',
        renderer: this.emptyClassRenderer
      },
      {
        data: 'music.genre2',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.winmediaData.genre2,
        allowInvalid: false,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.genre3',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.winmediaData.genre3,
        allowInvalid: false,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.mood1',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.winmediaData.mood1,
        allowInvalid: false,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.mood3',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.winmediaData.mood3,
        allowInvalid: false,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.tempo1',
        width: 35,
        type: 'dropdown',
        trimDropdown: false,
        source: this.winmediaData.tempo1,
        allowInvalid: false,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.label',
        width: 35,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.bpm',
        width: 20,
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.artist_image_url',
        width: 40,
        renderer: (instance, td, row, col, prop, value, cellProperties) =>
          this.coverRender(instance, td, row, col, prop, value, cellProperties,
            () => this.zone.run(() => this.searchArtistPicture(row, row)), 'Rechercher une image de l\'artiste'),
        className: 'htMiddle htCenter'
      },
      {
        data: 'music.rekordbox_dj',
        type: 'checkbox',
        width: 15,
        className: 'htMiddle htCenter'
      }
    ];
    this.setSettingsHotTable(configTable.hiddenColumns);
    this.initDataFiles(files);
    this.startAnalyzeFiles()
      .then(res => console.log('All fingerprints', res));
  }

  ngAfterViewInit() {
    this.selectedWaveformSub = this.dataPassService.selectedWaveform.subscribe(message => {
      this.selectedWaveform = message;
      const select = this.dataFiles.find(p => p.position === this.selectedWaveform);
      if (select) {
        this.dataFiles[select.position].status.waveform = 1;
      } else {
        for (const el of this.dataFiles) {
          el.status.waveform = 0;
        }
      }
      this.hotRegister.getInstance(this.hotId).render();
    });
  }

  ngOnDestroy() {
    this.selectedWaveformSub.unsubscribe();
    this.statusWaveformSub.unsubscribe();
    this.stopInterval();
  }

  initDataFiles(files) {
    let index = 0;
    this.dataPassService.musiques_mix = [];
    for (const file of files) {
      this.dataFiles.push({
        file: file.filename,
        folder: file.folder,
        position: index,
        status: {
          // 0 A faire / 1 Oui / 2 Non
          // fingerprint: 0,
          // spotify: 0,
          // gracenote: 0,
          // winmedia: 0,
          // subsonic: 0,
          // allReadonly: true,
          // readStat: true,
          present_subsonic: false,
          present_winmedia: false,
          array_subsonic: '[]',
          array_winmedia: '[]',
          bypass_subsonic: false,
          bypass_winmedia: false,
          general: 0,
          waveform: 0,
          waveformGenerated: file.statusWaveform ? file.statusWaveform : 0
        },
        music: {
          title: file.filename,
          stations: this.dataPassService.import_configTable.stations,
          diffusion: 'Toujours',
          rekordbox_dj: false
        },
        music_advanced: {},
        audio_analysis: {},
        metadata: file.metadata,
        automaticaly_selected_stations: [],
        automaticaly_selected_nouvo: false
      });
      this.dataPassService.musiques_mix.push({
        file: file.filename,
        folder: file.folder,
        mix: {
          fade: this.defaultFade.toString()
        }
      });
      index++;
    }
  }

  startAnalyzeFiles() {
    // return new Promise(async (resolve) => {
    return new Promise((resolve) => {
      const promises = [];
      const promises2 = [];
      let index = 0;

      console.log(this.dataFiles);

      for (const dataFile of this.dataFiles) {
        promises.push(this.analyzeFilesData(dataFile.folder, dataFile.file, index, dataFile.metadata));
        promises2.push(this.setDuration(dataFile.folder, dataFile.file, index));
        index++;
      }

      Promise.all(promises2)
        .then((results) => {
          console.log(results);
        });

      Promise.all(promises)
        .then((results) => {
          resolve(results);
        });
    });
  }

  setDuration(folder, file, index) {
    return new Promise((resolve, reject) => {
      this.analyzerService.fileMetadata(folder, file, index)
        .then(r => {
          if (r.error === 0) {
            this.dataPassService.musiques_mix[index].mix.duration = r.retour.format.duration;
            this.dataPassService.musiques_mix[index].mix.start = 0;
            this.dataPassService.musiques_mix[index].mix.mix = r.retour.format.duration - this.defaultFade;
          }
          resolve(r);
        }).catch(e => resolve(e));
    });
  }

  analyzeFilesData(folder, file, position, metadata) {
    return new Promise((resolve) => {
      if (metadata
        && metadata.title && metadata.title !== ''
        && metadata.artist && metadata.artist !== ''
        && metadata.album && metadata.album !== '') {
        // const artistRes = metadata.artist.replace(/\[.[^(]*]/g, '').replace(/\(.[^(]*\)/g, '').trim();
        // const trackRes = metadata.title.replace(/\[.[^(]*]/g, '').replace(/\(.[^(]*\)/g, '').trim();
        // const albumRes = metadata.album.replace(/\[.[^(]*]/g, '').replace(/\(.[^(]*\)/g, '').trim();
        const artistRes = metadata.artist.trim();
        const trackRes = metadata.title.trim();
        const albumRes = metadata.album.trim();
        const coverUrl = (metadata.cover_url && metadata.cover_url.trim().length > 0) ? metadata.cover_url : null;

        this.dataFiles[position].music.performer = artistRes;
        this.dataFiles[position].music.title = trackRes;
        this.dataFiles[position].music.album = albumRes;
        if (coverUrl) {
          this.dataFiles[position].music.cover = coverUrl;
        }

        this.musicsDataService.checkPresenceFichier(artistRes, trackRes)
          .then(r => {
            console.log('checkPresenceFichier', r);
            this.setPresence(false, position, r);
          })
          .catch(e => {
            console.warn('checkPresenceFichier', e);
            this.setPresence(true, position, e);
          });

        this.searchTrackMetadata(artistRes, trackRes, albumRes, metadata.isrc, position, coverUrl ? true : false);
        resolve(metadata);
      } else {
        this.analyzerService.acrcloudAnalyzeFingerprint(folder, file, position)
          .then(res => {
            if (res.error === 0) {
              /* Remove [***] and (***) */
              const artistRes = res.retour[0].artist.replace(/\[.[^(]*]/g, '').replace(/\(.[^(]*\)/g, '').trim();
              const trackRes = res.retour[0].track.replace(/\[.[^(]*]/g, '').replace(/\(.[^(]*\)/g, '').trim();
              const albumRes = res.retour[0].album.replace(/\[.[^(]*]/g, '').replace(/\(.[^(]*\)/g, '').trim();

              this.dataFiles[res.pos].music.performer = artistRes;
              this.dataFiles[res.pos].music.title = trackRes;
              this.dataFiles[res.pos].music.album = albumRes;
              this.hotRegister.getInstance(this.hotId).render();

              this.musicsDataService.checkPresenceFichier(artistRes, trackRes)
                .then(r => {
                  console.log('checkPresenceFichier', r);
                  this.setPresence(false, position, r);
                })
                .catch(e => {
                  console.warn('checkPresenceFichier', e);
                  this.setPresence(true, position, e);
                });

              this.searchTrackMetadata(artistRes, trackRes, albumRes, null, position);
              resolve(res);
            } else {
              this.dataFiles[position].status.general = 1;
              this.hotRegister.getInstance(this.hotId).render();
              resolve(res);
            }
          })
          .catch(err => {
            this.dataFiles[position].status.general = 1;
            this.hotRegister.getInstance(this.hotId).render();
            resolve(err);
          });
      }
    });
  }

  setPresence(failed= false, position, r) {
    if (!failed) {
      this.dataFiles[position].status.present_winmedia = (r.winmedia && r.winmedia.length > 0);
      this.dataFiles[position].status.array_winmedia = JSON.stringify(r.winmedia);
      this.dataFiles[position].status.present_subsonic = (r.subsonic && r.subsonic.length > 0);
      this.dataFiles[position].status.array_subsonic = JSON.stringify(r.subsonic);
    } else {
      this.dataFiles[position].status.present_winmedia = false;
      this.dataFiles[position].status.array_winmedia = '[]';
      this.dataFiles[position].status.present_subsonic = false;
      this.dataFiles[position].status.array_subsonic = '[]';
    }
    this.dataFiles[position].status.bypass_winmedia = false;
    this.dataFiles[position].status.bypass_subsonic = false;
  }

  searchTrackMetadata(artist, track, album, isrc, position, picturePresent = false) {
    this.musicsDataService.searchTrackMetadata(artist, track, album, isrc)
      .then(r => {
        const { proposal } = this.buildMetadataProposal(position, r, picturePresent);
        this.applyMetadataProposal(position, proposal, Object.keys(proposal) as (keyof MetadataProposal)[]);
        this.dataFiles[position].status.general = 1;
        this.hotRegister.getInstance(this.hotId).render();
      })
      .catch(e => {
        console.warn('searchTrackMetadata', e);
        this.dataFiles[position].status.general = 1;
        this.hotRegister.getInstance(this.hotId).render();
      });
  }

  /**
   * Calcule ce que le mix des sources (Spotify, Deezer, MusicBrainz) propose pour une ligne,
   * sans rien écrire dans dataFiles ni dans les listes globales (winmediaData.genre2/3, countries) :
   * cette fonction est pure pour pouvoir être utilisée aussi bien par la passe automatique (tout
   * appliquer directement) que par la review manuelle (l'utilisateur choisit quoi appliquer).
   */
  buildMetadataProposal(position: number, r: MetadataSearch, picturePresent = false, spotifyCandidateIndex = 0,
                         includeUnconfirmed = false): MetadataProposalResult {
    const music = this.dataFiles[position].music;
    const musicAdvanced = this.dataFiles[position].music_advanced;
    const proposal: MetadataProposal = {};
    let spotifyCandidates: SpotifyCandidateSummary[] | undefined;
    let spotifyIsrc: string | undefined;

    // INSTAGRAM : priorité à la valeur déjà connue en base WinMedia (déjà confirmée pour cet artiste
    // lors d'un import précédent, pas besoin de refaire confiance à une nouvelle recherche MusicBrainz
    // "best effort"), sinon repli sur la relation url-rels retrouvée par MusicBrainz (extractInstagramUrl,
    // voir plus bas où musicbrainzResult est lu). Indépendant du statut MusicBrainz, comme twitter avant.
    const winmediaInstagram = r.winmediaInstagram?.length > 0 ? r.winmediaInstagram[0]._instagram : null;
    const mbInstagram = r.musicbrainz.status ? r.musicbrainz.result.artists?.[0]?.instagram : null;
    const proposedInstagram = winmediaInstagram || mbInstagram;
    if (proposedInstagram) {
      proposal.instagram = { current: music.instagram || '', proposed: proposedInstagram, changed: music.instagram !== proposedInstagram };
    }

    let labelPresent = false;
    let releaseDatePresent = false;

    if (r.spotify.status || r.spotifyByISRC.status) {
      const spotifyResult = r.spotifyByISRC.status ? r.spotifyByISRC.result : r.spotify.result;
      const candidates = spotifyResult.candidates || [];

      if (candidates.length > 0) {
        // Résumé des candidats pour la modale de review : elle peut recalculer cette fonction avec un
        // autre spotifyCandidateIndex si le mieux noté (index 0) ne correspond pas au bon morceau.
        spotifyCandidates = candidates.map((c, index) => ({
          index,
          score: c.score,
          artist: c.track.artists.map(a => a.name).join(', '),
          title: c.track.name,
          album: c.album.name,
          year: c.album.release_date ? c.album.release_date.slice(0, 4) : '',
          cover: c.track.album.images?.[0]?.url,
          label: c.album.label
        }));

        const chosen = candidates[Math.min(spotifyCandidateIndex, candidates.length - 1)];
        const track = chosen.track;
        spotifyIsrc = track.external_ids?.isrc;
        const albumData = chosen.album;

        // LABEL
        if (albumData.label) {
          proposal.label = { current: music.label, proposed: albumData.label, changed: music.label !== albumData.label };
          labelPresent = true;
        }

        // YEAR & RELEASE DATE
        if (albumData.release_date) {
          const proposed: ReleaseDateValue = {
            year: albumData.release_date.slice(0, 4),
            release_date: albumData.release_date
          };
          proposal.releaseDate = { current: { year: music.year, release_date: music.release_date }, proposed, changed: music.release_date !== proposed.release_date };
          releaseDatePresent = true;
        }

        // ALBUM (use Spotify if present, more coherent with single)
        if (albumData.name) {
          proposal.album = { current: music.album, proposed: albumData.name, changed: music.album !== albumData.name };
        }

        // TITRE & ARTISTE : reprend la graphie exacte trouvée par Spotify (utile surtout pour rattraper
        // une différence de casse — ex. "get lucky" saisi/détecté vs "Get Lucky" chez Spotify).
        if (track.name) {
          proposal.title = { current: music.title, proposed: track.name, changed: music.title !== track.name };
        }
        if (track.artists.length > 0) {
          const proposedArtist = track.artists.map(a => a.name).join(', ');
          proposal.artist = { current: music.performer || '', proposed: proposedArtist, changed: music.performer !== proposedArtist };
        }

        // NUMBER and COVER
        proposal.discTrackNumber = {
          current: { disc_number_spotify: musicAdvanced.disc_number_spotify, track_number_spotify: musicAdvanced.track_number_spotify },
          proposed: { disc_number_spotify: track.disc_number, track_number_spotify: track.track_number },
          changed: true
        };

        if (picturePresent === false && track.album.images && track.album.images.length > 0) {
          proposal.cover = { current: music.cover, proposed: track.album.images[0].url, changed: music.cover !== track.album.images[0].url };
          picturePresent = true;
        }
      }
    }
    if (r.currentArtistPicture !== null) {
      proposal.artistImage = { current: music.artist_image_url, proposed: r.currentArtistPicture, changed: music.artist_image_url !== r.currentArtistPicture };
    }
    if (r.deezer.status) {
      const deezerResult = r.deezer.result;

      // ARTISTES SIMILAIRES
      if (deezerResult.related_artists && deezerResult.related_artists.data && deezerResult.related_artists.data.length > 0) {
        let artSim = '';
        for (let y = 0; y < deezerResult.related_artists.data.length; y++) {
          artSim += this.CF.remove_emojis(deezerResult.related_artists.data[y].name).trim();
          if (y < (deezerResult.related_artists.data.length - 1)) {
            artSim += '/';
          }
        }
        proposal.similarArtist = { current: musicAdvanced.similar_artist, proposed: artSim, changed: musicAdvanced.similar_artist !== artSim };
      }

      // YEAR & RELEASE DATE (repli si Spotify n'a rien fourni)
      if (deezerResult.release_date && releaseDatePresent === false) {
        const proposed: ReleaseDateValue = { year: deezerResult.release_date.slice(0, 4), release_date: deezerResult.release_date };
        proposal.releaseDate = { current: { year: music.year, release_date: music.release_date }, proposed, changed: music.release_date !== proposed.release_date };
        releaseDatePresent = true;
      }

      // GENRES
      if (deezerResult.genres && deezerResult.genres.data && deezerResult.genres.data.length > 0) {
        const genreProposed = this.resolveGenreProposal(deezerResult.genres.data);
        const changed = music.genre1 !== genreProposed.genre1 || music.genre2 !== genreProposed.genre2 || music.genre3 !== genreProposed.genre3;
        proposal.genre = { current: { genre1: music.genre1, genre2: music.genre2, genre3: music.genre3 }, proposed: genreProposed, changed };
      }

      // LABEL (repli)
      if (labelPresent === false && deezerResult.label) {
        proposal.label = { current: music.label, proposed: deezerResult.label, changed: music.label !== deezerResult.label };
        labelPresent = true;
      }

      // COVER (repli)
      if (picturePresent === false && deezerResult.cover_big) {
        proposal.cover = { current: music.cover, proposed: deezerResult.cover_big, changed: music.cover !== deezerResult.cover_big };
        picturePresent = true;
      }

      // IMAGE ARTISTE (repli si aucune photo courante trouvée)
      if (deezerResult.artist && deezerResult.artist.name !== "Various Artists" && r.currentArtistPicture === null) {
        const img = deezerResult.artist.picture_xl || deezerResult.artist.picture_big;
        if (img) {
          proposal.artistImage = { current: music.artist_image_url, proposed: img, changed: music.artist_image_url !== img };
        }
      }
    }
    if (r.musicbrainz.status) {
      const musicbrainzResult = r.musicbrainz.result;
      // NATIONALITE
      const artist = musicbrainzResult.artists?.[0];

      const countryData = artist?.countryInfo?.type === 'Country'
        ? artist.countryInfo
        : (artist?.area?.type === 'Country' ? artist.area : null);

      if (countryData?.name) {
        const countryJSON = this.countriesJSON.find(c => c.name.common === countryData.name);
        const commonCountryName = countryJSON?.translations?.fra?.common || countryData.name;

        // VE/VF
        const frenchSpeakingCountries = ['France', 'Belgique', 'Canada', 'Luxembourg', 'Suisse'];
        const proposedVef = frenchSpeakingCountries.includes(commonCountryName) ? 'Française' : 'Etrangère';

        const currentCountry = this.countries.find(c => c.id === +music.flag);
        proposal.nationality = {
          current: { countryName: currentCountry?.label, vef: music.vef },
          proposed: { countryName: commonCountryName, vef: proposedVef },
          changed: currentCountry?.label !== commonCountryName || music.vef !== proposedVef
        };
      }
    }

    // ISRC / IDENTIFIANTS MUSICBRAINZ : en vue d'une association fiable avec des services externes plus
    // tard (voir discussion isrc → mbApi ; pas encore persisté au-delà de la session, juste champ caché
    // + log pour le moment). L'ISRC peut être connu (tag/Deezer) sans que MusicBrainz ait matché quoi
    // que ce soit, et inversement mbArtistId/mbRecordingId ne remontent que sur une confiance suffisante.
    {
      const mbResult = r.musicbrainz.status ? r.musicbrainz.result : null;
      const proposedIds: MusicbrainzIdsValue = {
        isrc: spotifyIsrc || undefined,
        artistId: mbResult?.mbArtistId || undefined,
        recordingId: mbResult?.mbRecordingId || undefined,
        matchConfidence: mbResult?.mbMatchConfidence || undefined
      };
      if (proposedIds.isrc || proposedIds.artistId || proposedIds.recordingId) {
        const current: MusicbrainzIdsValue = {
          isrc: musicAdvanced.isrc,
          artistId: musicAdvanced.musicbrainz_artist_id,
          recordingId: musicAdvanced.musicbrainz_recording_id
        };
        console.log('[MusicBrainz IDs]', { position, artist: music.performer, title: music.title, ...proposedIds });
        proposal.musicbrainzIds = {
          current,
          proposed: proposedIds,
          changed: current.isrc !== proposedIds.isrc || current.artistId !== proposedIds.artistId || current.recordingId !== proposedIds.recordingId
        };
      }
    }

    if (includeUnconfirmed) {
      // Signale les champs pour lesquels rien n'a été retrouvé cette fois-ci, alors qu'une valeur est
      // déjà présente : sans ça, une valeur potentiellement erronée (ancienne mauvaise association)
      // reste invisible dans la review, comme si tout ce qui n'est pas montré était forcément correct.
      // Uniquement pour la review manuelle : la passe automatique ne doit jamais vider un champ juste
      // parce qu'une source a raté ce coup-ci.
      for (const field of METADATA_PROPOSAL_REVIEWABLE_FIELDS) {
        if (!proposal[field]) {
          const current = this.getCurrentValueForReviewField(field, music);
          if (current !== undefined && current !== '') {
            (proposal[field] as any) = { current, proposed: undefined, changed: false };
          }
        }
      }
    }

    return { proposal, spotifyCandidates, spotifyCandidateIndex };
  }

  private getCurrentValueForReviewField(field: keyof MetadataProposal, music: Musiques['music']): any {
    switch (field) {
      case 'album': return music.album;
      case 'label': return music.label;
      case 'cover': return music.cover;
      case 'artistImage': return music.artist_image_url;
      case 'releaseDate':
        return music.release_date ? { year: music.year, release_date: music.release_date } : undefined;
      case 'genre':
        return (music.genre1 || music.genre2 || music.genre3)
          ? { genre1: music.genre1, genre2: music.genre2, genre3: music.genre3 } : undefined;
      case 'nationality': {
        const currentCountry = this.countries.find(c => c.id === +music.flag);
        return currentCountry ? { countryName: currentCountry.label, vef: music.vef } : undefined;
      }
      default: return undefined;
    }
  }

  /* Reproduit la logique de correspondance genre Deezer -> genres Winmedia (4 cas, du plus précis
   * au plus permissif), en se contentant de renvoyer la décision : aucune écriture dans dataFiles
   * ni dans winmediaData.genre2/3 ici, ça reste à la charge d'applyMetadataProposal. */
  resolveGenreProposal(genresData: any[]): GenreValue {
    // @ts-ignore
    if (this.winmediaData.genre.includes(genresData[0].name)) {
      return { genre1: genresData[0].name, genre2: genresData[1]?.name, genre3: genresData[2]?.name };
    }
    // @ts-ignore
    if (genresData[1] && this.winmediaData.genre.includes(genresData[1].name)) {
      return { genre1: genresData[1].name, genre2: genresData[2]?.name, genre3: genresData[3]?.name };
    }

    for (const corres of this.genresAssociation) {
      if (corres.source.includes(genresData[0].name)) {
        return { genre1: corres.destination, genre2: genresData[0].name, genre3: genresData[1]?.name };
      }
    }

    const possibilitiesWinGenre = [];
    // @ts-ignore
    for (const genreWinmedia of this.winmediaData.genre) {
      if (genresData[0].name.includes(genreWinmedia)) {
        possibilitiesWinGenre.push(genreWinmedia);
      }
    }
    let indexFind = 1000;
    let nameFind = '';
    for (const possibility of possibilitiesWinGenre) {
      const pos = genresData[0].name.indexOf(possibility);
      if (pos < indexFind) {
        indexFind = pos;
        nameFind = possibility;
      }
    }

    return { genre1: nameFind !== '' ? nameFind : undefined, genre2: genresData[0].name, genre3: genresData[1]?.name };
  }

  /**
   * Écrit dans dataFiles (et déclenche les effets de bord : association stations, alimentation des
   * listes déroulantes de genres, création d'un pays dans `countries`) uniquement les champs listés
   * dans fieldsToApply. Utilisée avec tous les champs du proposal en mode automatique, et avec les
   * champs cochés par l'utilisateur en mode review manuelle.
   */
  applyMetadataProposal(position: number, proposal: MetadataProposal, fieldsToApply: (keyof MetadataProposal)[]) {
    const music = this.dataFiles[position].music;
    const musicAdvanced = this.dataFiles[position].music_advanced || (this.dataFiles[position].music_advanced = {});

    // Repart de zéro sur les champs invisibles alimentés par la recherche : si rien n'est retrouvé
    // cette fois, on ne garde pas une ancienne valeur (potentiellement issue d'une mauvaise association
    // passée) qui resterait invisible pour l'utilisateur. Fait ici (à l'application, pas avant) pour
    // qu'annuler la review manuelle ne fasse perdre aucune donnée.
    musicAdvanced.similar_artist = undefined;
    musicAdvanced.disc_number_spotify = undefined;
    musicAdvanced.track_number_spotify = undefined;
    musicAdvanced.isrc = undefined;
    musicAdvanced.musicbrainz_artist_id = undefined;
    musicAdvanced.musicbrainz_recording_id = undefined;

    for (const field of fieldsToApply) {
      const entry = proposal[field];
      if (!entry) { continue; }

      switch (field) {
        case 'title':
          music.title = entry.proposed as string;
          break;
        case 'artist':
          music.performer = entry.proposed as string;
          break;
        case 'instagram':
          music.instagram = entry.proposed as string;
          break;
        case 'label':
          music.label = entry.proposed as string;
          break;
        case 'releaseDate': {
          // proposed peut être undefined (champ "vidé" depuis la modale, cf. includeUnconfirmed)
          const value = entry.proposed as ReleaseDateValue | undefined;
          music.year = value?.year;
          music.release_date = value?.release_date;
          break;
        }
        case 'album':
          music.album = entry.proposed as string;
          break;
        case 'discTrackNumber': {
          const value = entry.proposed as DiscTrackNumberValue;
          musicAdvanced.disc_number_spotify = value.disc_number_spotify;
          musicAdvanced.track_number_spotify = value.track_number_spotify;
          break;
        }
        case 'cover':
          music.cover = entry.proposed as string;
          break;
        case 'artistImage':
          music.artist_image_url = entry.proposed as string;
          break;
        case 'similarArtist':
          musicAdvanced.similar_artist = entry.proposed as string;
          break;
        case 'musicbrainzIds': {
          const value = entry.proposed as MusicbrainzIdsValue | undefined;
          musicAdvanced.isrc = value?.isrc;
          musicAdvanced.musicbrainz_artist_id = value?.artistId;
          musicAdvanced.musicbrainz_recording_id = value?.recordingId;
          break;
        }
        case 'genre': {
          // proposed peut être undefined (champ "vidé" depuis la modale, cf. includeUnconfirmed)
          const value = entry.proposed as GenreValue | undefined;
          music.genre1 = value?.genre1;
          if (value?.genre2) {
            music.genre2 = value.genre2;
            // @ts-ignore
            if (!this.winmediaData.genre2.includes(value.genre2)) {
              // @ts-ignore
              this.winmediaData.genre2.push(value.genre2);
            }
          } else {
            music.genre2 = undefined;
          }
          if (value?.genre3) {
            music.genre3 = value.genre3;
            // @ts-ignore
            if (!this.winmediaData.genre3.includes(value.genre3)) {
              // @ts-ignore
              this.winmediaData.genre3.push(value.genre3);
            }
          } else {
            music.genre3 = undefined;
          }
          this.updateStationsFromGenre(position, music.genre1 ? music.genre1.trim() : '');
          break;
        }
        case 'nationality': {
          // proposed peut être undefined (champ "vidé" depuis la modale, cf. includeUnconfirmed)
          const value = entry.proposed as NationalityValue | undefined;
          if (!value) {
            music.flag = undefined;
            music.vef = undefined;
            break;
          }
          let existingCountry = this.countries.find(c => c.label === value.countryName);
          if (!existingCountry) {
            existingCountry = { id: this.countries.length, label: value.countryName };
            this.countries.push(existingCountry);
          }
          music.flag = existingCountry.id + '';
          music.vef = value.vef;
          break;
        }
      }
    }

    this.hotRegister.getInstance(this.hotId).render();
  }

  addCover(start, end, type) {
    const dialogRef = this.matDialog.open(CoverUploadComponent, {
      width: '350px',
      data: {startRow: start, endRow: end},
      panelClass: 'coverPanel'
    });

    dialogRef.afterClosed().toPromise()
      .then(result => {
        console.log(result);
        if (result && result.edit) {
          for (let i = result.startRow; i <= result.endRow; i++) {
            if (type === 'cover') {
              this.dataFiles[i].music.cover = result.cover;
            }
            if (type === 'artist_picture') {
              this.dataFiles[i].music.artist_image_url = result.cover;
            }
          }
          this.hotRegister.getInstance(this.hotId).render();
        }
      });
  }

  searchDiscogsCover(start, end) {
    let search = '';
    if (this.dataFiles[start] && this.dataFiles[start].music
      && this.dataFiles[start].music.performer && this.dataFiles[start].music.title) {
      search = this.dataFiles[start].music.performer
        + ' ' + this.dataFiles[start].music.title;
    }
    const dialogRefDiscogs = this.matDialog.open(CoverSearchComponent, {
      data: {
        startRow: start,
        endRow: end,
        search: search
      },
      panelClass: 'discogsPanel'
    });

    dialogRefDiscogs.afterClosed().toPromise()
      .then(result => {
        console.log(result);
        if (result && result.edit) {
          for (let i = result.startRow; i <= result.endRow; i++) {
            this.dataFiles[i].music.cover = result.cover;
            if (result.year && result.year.length > 0) {
              this.dataFiles[i].music.year = result.year;
            }
            if (result.album && result.album.length > 0) {
              this.dataFiles[i].music.album = result.album;
            }
          }
          this.hotRegister.getInstance(this.hotId).render();
        }
      });
  }

  searchTmdbCover(start, end) {
    let search = '';
    if (this.dataFiles[start] && this.dataFiles[start].music
      && this.dataFiles[start].music.performer) {
      search = this.dataFiles[start].music.performer;
    }
    const dialogRefTmdb = this.matDialog.open(TmdbSearchComponent, {
      data: {
        startRow: start,
        endRow: end,
        search: search
      },
      panelClass: 'tmdbPanel'
    });

    dialogRefTmdb.afterClosed().toPromise()
      .then(result => {
        if (result && result.edit) {
          for (let i = result.startRow; i <= result.endRow; i++) {
            this.dataFiles[i].music.cover = result.cover;
            if (result.year && result.year.length > 0) {
              this.dataFiles[i].music.year = result.year;
            }
          }
          this.hotRegister.getInstance(this.hotId).render();
        }
      });
  }

  searchArtistPicture(start, end) {
    const dialogRefDiscogs = this.matDialog.open(ArtistPictureSearch, {
      data: {
        startRow: start,
        endRow: end,
        search: this.dataFiles[start].music.performer
      },
      panelClass: 'discogsPanel'
    });

    dialogRefDiscogs.afterClosed().toPromise()
      .then(result => {
        console.log(result);
        if (result && result.edit) {
          for (let i = result.startRow; i <= result.endRow; i++) {
            this.dataFiles[i].music.artist_image_url = result.cover;
          }
          this.hotRegister.getInstance(this.hotId).render();
        }
      });
  }

  searchArtistInstagram(start, end) {
    const dialogRefInstagram = this.matDialog.open(ArtistInstagramSearch, {
      data: {
        startRow: start,
        endRow: end,
        search: this.dataFiles[start].music.performer
      },
      panelClass: 'discogsPanel'
    });

    dialogRefInstagram.afterClosed().toPromise()
      .then(result => {
        if (result && result.edit) {
          for (let i = result.startRow; i <= result.endRow; i++) {
            this.dataFiles[i].music.instagram = result.instagram;
          }
          this.hotRegister.getInstance(this.hotId).render();
        }
      });
  }

  choixPresence(row) {
    const dialogRef = this.matDialog.open(ChoiceWinmediaComponent, {
      data: {row: row, winmedia: JSON.parse(this.dataFiles[row].status.array_winmedia)},
      panelClass: 'choicePanel'
    });

    dialogRef.afterClosed().toPromise()
      .then(result => {
        if (result && result.userAction) {
          if (result.newUpload) {
            this.dataFiles[row].status.bypass_winmedia = true;
          } else {
            this.dataFiles[row].status.bypass_winmedia = true;
            this.dataFiles[row].status.general = 5;
          }
        }
      });
  }

  /* "Analyse metadonnées" (clic droit, une seule ligne) : relance la recherche à partir du
   * titre/artiste/album déjà en table, mais affiche une modale de review avant d'appliquer quoi que
   * ce soit — contrairement à la passe automatique (import) qui applique tout directement. Les champs
   * à faible risque (instagram, n° piste/disque Spotify, artistes similaires) restent appliqués
   * silencieusement, comme convenu. */
  reloadMetadataWithReview(row: number) {
    const music = this.dataFiles[row].music;
    if (!music.performer || !music.title || !music.album) {
      const missing = [];
      if (!music.performer) { missing.push('artiste'); }
      if (!music.title) { missing.push('titre'); }
      if (!music.album) { missing.push('album'); }
      this.snackBar.open(`Analyse impossible : champ${missing.length > 1 ? 's' : ''} manquant${missing.length > 1 ? 's' : ''} (${missing.join(', ')}).`, '', { duration: 4000 });
      return;
    }

    // music_advanced n'est volontairement pas remis à zéro ici : si l'utilisateur annule la modale,
    // les valeurs déjà présentes (artistes similaires, n° piste/disque) ne doivent pas être perdues.
    // Le nettoyage a lieu dans applyMetadataProposal, seulement si l'utilisateur valide.
    this.dataFiles[row].status.general = 0;
    this.dataFiles[row].status.present_winmedia = false;
    this.dataFiles[row].status.array_winmedia = '[]';
    this.dataFiles[row].status.present_subsonic = false;
    this.dataFiles[row].status.array_subsonic = '[]';
    this.hotRegister.getInstance(this.hotId).render();

    this.musicsDataService.checkPresenceFichier(music.performer, music.title)
      .then(r => this.setPresence(false, row, r))
      .catch(e => this.setPresence(true, row, e));

    // La recherche (Deezer/Spotify/MusicBrainz) peut prendre plusieurs secondes : la modale s'ouvre
    // tout de suite avec un spinner plutôt que de laisser l'utilisateur sans retour visuel en attendant.
    let searchResult: MetadataSearch | null = null;
    let resolvedProposal: MetadataProposal | null = null;

    const resultPromise = this.musicsDataService.searchTrackMetadata(music.performer, music.title, music.album, null)
      .then(r => {
        searchResult = r;
        const result = this.buildMetadataProposal(row, r, false, 0, true);
        resolvedProposal = result.proposal;
        return result;
      })
      .catch(e => {
        console.warn('reloadMetadataWithReview', e);
        throw e;
      });

    // Permet à la modale de recalculer le diff si l'utilisateur choisit un autre candidat Spotify
    // que celui pré-sélectionné (le mieux noté) — recalcul pur, aucun nouvel appel réseau.
    const recomputeForCandidate = (candidateIndex: number): MetadataProposalResult => {
      const result = this.buildMetadataProposal(row, searchResult!, false, candidateIndex, true);
      resolvedProposal = result.proposal;
      return result;
    };

    const dialogRef = this.matDialog.open(MetadataReviewComponent, {
      width: '700px',
      data: { resultPromise, recomputeForCandidate },
      panelClass: 'metadataReviewPanel'
    });

    dialogRef.afterClosed().toPromise()
      .then((fieldsToApply: (keyof MetadataProposal)[] | null) => {
        if (fieldsToApply && resolvedProposal) {
          const proposal = resolvedProposal;
          const silentFields = METADATA_PROPOSAL_SILENT_FIELDS.filter(field => !!proposal[field]);
          this.applyMetadataProposal(row, proposal, [...silentFields, ...fieldsToApply]);
        }
        this.dataFiles[row].status.general = 1;
        this.hotRegister.getInstance(this.hotId).render();
      });
  }

  onAfterChange(dataChange) {
    console.log('onAfterChange');
    console.log(dataChange);
  }

  clickChangeData() {
    this.dataFiles[0].music.performer = 'New value 1';
    this.dataFiles[1].music.title = 'New value 1';
  }

  logData() {
    console.log('dataFiles', this.dataFiles);
    console.log('musiques_mix', this.dataPassService.musiques_mix);
    console.log('user', this.authentificationService.userSource.getValue());
    console.log('ids', this.mysqlSyncService.dataID);
    this.mysqlSyncService.syncImport(this.dataFiles, this.dataPassService.musiques_mix)
      .then(res => console.log(res))
      .catch(err => console.log(err));
  }

  removeRow() {
    this.dataFiles.splice(1, 1);
  }

  statusRender(instance, td, row, col, prop, value, cellProperties, dataFiles) {
    let img = null;
    if (dataFiles && dataFiles.length > 0 && dataFiles[row].status.present_winmedia && !dataFiles[row].status.bypass_winmedia) {
      img = document.createElement('IMG');
      img.src = 'assets/icon/warning2.png';
    } else if (dataFiles && dataFiles.length > 0 && dataFiles[row].status.waveform === 1) {
      img = document.createElement('IMG');
      img.src = 'assets/icon/encoursSelected.png';
    } else {
      switch (value) {
        case 0:
          img = document.createElement('IMG');
          img.src = 'assets/icon/load.gif';
          break;
        case 1:
          img = document.createElement('IMG');
          if (dataFiles[row].status.waveformGenerated === 1) {
            img.src = 'assets/icon/encours.png';
          } else {
            img.src = 'assets/icon/encoursWait.png';
          }
          break;
        case 2:
          img = document.createElement('IMG');
          img.src = 'assets/icon/warning.png';
          break;
        case 3:
          img = document.createElement('IMG');
          img.src = 'assets/icon/sendGRAY.gif';
          break;
        case 4:
          img = document.createElement('IMG');
          img.src = 'assets/icon/send.gif';
          break;
        case 5:
          img = document.createElement('IMG');
          img.src = 'assets/icon/valid.png';
          break;
        case 6:
          img = document.createElement('IMG');
          img.src = 'assets/icon/error.png';
          break;
        default:
          img = document.createElement('IMG');
          img.src = 'assets/icon/error.png';
          break;
      }
    }

    img.width = 30;
    img.style.paddingTop = '9px';
    img.style.paddingBottom = '10px';
    img.style.margin = 'auto';
    img.style.display = 'block';

    Handsontable.default.dom.addEvent(img, 'mousedown', function(event) {
      event.preventDefault();
    });

    Handsontable.default.dom.empty(td);
    td.appendChild(img);

    return td;
  }

  coverRender(instance, td, row, col, prop, value, cellProperties, onSearchClick?, searchTitle?) {
    const escaped = Handsontable.default.helper.stringify(value);
    let img = null;

    if (escaped.indexOf('http') === 0) {
      img = document.createElement('IMG');
      img.src = value;

      img.style.width = '40px';
      img.style.height = '40px';
      img.style.objectFit = 'contain';
      img.style.paddingTop = '4px';
      img.style.paddingBottom = '5px';
      img.style.margin = 'auto';
      img.style.display = 'block';

      Handsontable.default.dom.addEvent(img, 'mousedown', function(event) {
        event.preventDefault();
      });

      Handsontable.default.dom.addEvent(img, 'mouseover', function(event) {
        event.preventDefault();
        Controls.mouseOverListener(event.target);
      });

      Handsontable.default.dom.addEvent(img, 'mouseout', function(event) {
        event.preventDefault();
        Controls.mouseOutListener();
      });

      Handsontable.default.dom.empty(td);
      td.appendChild(img);

    } else {
      Handsontable.default.renderers.TextRenderer.call(this, instance, td, row, col, prop, value, cellProperties);
      if (prop === 'music.cover') {
        td.style.background = '#CC5959';
      }
    }

    if (onSearchClick) {
      appendSearchIcon(td, onSearchClick, searchTitle);
    }

    return td;
  }

  /* Test : icône de recherche au survol sur une seule colonne (Instagram) avant de l'étendre
   * éventuellement à Cover/Photo artiste (coverRender) une fois validé. */
  instagramRenderer(instance, td, row, col, prop, value, cellProperties, onSearchClick, searchTitle) {
    Handsontable.default.renderers.TextRenderer.call(this, instance, td, row, col, prop, value, cellProperties);
    appendSearchIcon(td, onSearchClick, searchTitle);
    return td;
  }

  flagRenderer(instance, td, row, col, prop, value, cellProperties, countriesJSON) {
    while (td.firstChild) {
      td.removeChild(td.firstChild);
    }
    // Fix mais n'affiche pas de drapeau -> à voir solution : drapeau générique ?
    if (value && value !== '' && countriesJSON[Number(value)] && countriesJSON[Number(value)].cca2) {
      const cca2min = (countriesJSON[Number(value)].cca2).toLowerCase();
      const flagElement = document.createElement('span');
      flagElement.className = 'flag-icon flag-icon-' + cca2min;
      td.appendChild(flagElement);
    }
  }

  customDropdownRenderer(instance, td, row, col, prop, value, cellProperties) {
    let selectedId;
    const optionsList = cellProperties.chosenOptions.data;

    if (typeof optionsList === 'undefined' || typeof optionsList.length === 'undefined' || !optionsList.length) {
      Handsontable.default.cellTypes.text.renderer(instance, td, row, col, prop, value, cellProperties);
      return td;
    }

    const values = (value + '').split(',');
    value = [];
    for (let index = 0; index < optionsList.length; index++) {

      if (values.indexOf(optionsList[index].id + '') > -1) {
        selectedId = optionsList[index].id;
        value.push(optionsList[index].label);
      }
    }
    value = value.join(', ');

    Handsontable.default.cellTypes.text.renderer(instance, td, row, col, prop, value, cellProperties);
    return td;
  }

  emptyClassRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.default.renderers.TextRenderer.apply(this, arguments);
    if (!value || value === '') {
      td.style.background = '#CC5959';
    }
  }

  releaseDateEmptyRenderer(instance, td, row, col, prop, value, cellProperties, dataFiles, proprietes) {
    const isNew = dataFiles[row].music.proprietes ? dataFiles[row].music.proprietes.split(',').map(Number).includes(proprietes.find(x => x.label === 'Nouveauté').id) : false;

    Handsontable.default.renderers.TextRenderer.apply(this, arguments);
    if ((!value || value === '') && isNew) {
      td.style.background = '#CC5959';
    }
  }

  setSettingsHotTable(hiddenColumns) {
    this.hotSettings = {
      data: this.dataFiles,
      // observeChanges: true,
      afterChange: (changes) => {
        console.log(changes);
        console.log('proprietes', this.proprietes);
        if (changes) {
          changes.forEach(([row, prop, oldValue, newValue]) => {
            if (prop === 'music.genre1' && oldValue !== newValue) {
              if (newValue) {
                this.updateStationsFromGenre(row, newValue.trim());
              } else {
                this.updateStationsFromGenre(row, '');
              }
            } else if (prop === 'music.proprietes' && oldValue !== newValue) {
              if (newValue) {
                const isNouvo = newValue.split(',').map(Number).includes(this.proprietes.find(x => x.label === 'Nouveauté').id);
                this.updateStationsFromNewProperty(row, isNouvo);

                // Add "Grand Ouest" if "Pays De La Loire" is selected
                const artistGOId = this.proprietes.find(x => x.label === 'Grand Ouest')?.id;
                const artistPDLLId = this.proprietes.find(x => x.label === 'Pays De La Loire')?.id;
                if (artistGOId && artistPDLLId) {
                  const currentProps = newValue.split(',').map(Number);
                  const hasArtistGO = currentProps.includes(artistGOId);
                  const hasArtistPDLL = currentProps.includes(artistPDLLId);

                  if (hasArtistPDLL && !hasArtistGO) {
                    currentProps.push(artistGOId);
                    this.dataFiles[row].music.proprietes = currentProps.join(',');
                    this.hotRegister.getInstance(this.hotId).render();
                  }
                }
              } else {
                this.updateStationsFromNewProperty(row, false);
              }
            } else if (prop === 'music.instagram' && oldValue !== newValue) {
              // Force la saisie manuelle au format "@pseudo", quelle que soit la forme tapée/collée
              // (lien, #pseudo, pseudo seul) — voir CommonFunctionsService.formatInstagramHandle.
              const formatted = this.CF.formatInstagramHandle(newValue) || undefined;
              if (formatted !== newValue) {
                this.dataFiles[row].music.instagram = formatted;
                this.hotRegister.getInstance(this.hotId).render();
              }
            }
          });
        }
        this.needSync = true;
      },
      /*afterChangesObserved: () => {
        this.onAfterChange(this.dataFiles);
      },*/
      afterSelection: (row, column) => {
        this.cp_row = row;
        this.cp_column = column;
      },
      afterOnCellMouseDown: (event, coords) => {
        if (coords.col === 0) {
          if (this.dataFiles[coords.row].status.present_winmedia && !this.dataFiles[coords.row].status.bypass_winmedia) {
            this.choixPresence(coords.row);
          } else if (this.dataFiles[coords.row].status.general === 1 && this.dataFiles[coords.row].status.waveformGenerated === 1) {
            this.waveform(coords.row);
          }
        }
      },
      colHeaders: this.colHeaders,
      columns: this.columns,
      contextMenu: this.contextMenu,
      stretchH: 'all',
      wordWrap: false,
      columnSorting: false,
      // Gouttière dédiée à gauche (indépendante des colonnes de données, donc sans impact sur les
      // index utilisés par hiddencolumns) : pas de numéro de ligne affiché, juste l'icône d'analyse
      // métadonnées (voir afterGetRowHeader) — un repère visuel clair, distinct des icônes de
      // recherche posées dans les cellules (Cover/Photo artiste/Instagram).
      rowHeaders: true,
      rowHeaderWidth: 24,
      afterGetRowHeader: (row, TH) => {
        TH.textContent = '';
        // status.general === 1 : métadonnées réceptionnées, fichier en attente de validation/envoi
        // (voir sendValidatedFiles) — c'est le seul moment où relancer l'analyse a un sens pour
        // l'utilisateur, donc l'icône n'est affichée que dans cet état (fixe, pas seulement au survol).
        if (this.dataFiles[row]?.status?.general === 1) {
          appendSearchIcon(TH, () => this.zone.run(() => this.reloadMetadataWithReview(row)), 'Analyser les métadonnées', '🔄',
            { alwaysVisible: true, centered: true });
        }
      },
      manualColumnResize: true,
      currentRowClassName: 'currentRow',
      currentColClassName: 'currentCol',
      hiddenColumns: {
        indicators: false,
        columns: hiddenColumns
      },
      licenseKey: 'non-commercial-and-evaluation'
    };
  }

  updateStationsFromGenre(row: number, newGenre: string) {
    // Récupère les stations actuelles en excluant celles qui ont été ajoutées automatiquement
    // (music.stations peut être null si la cellule a été vidée, pas juste une chaîne vide)
    const currentStations = (this.dataFiles[row].music.stations || '').split(',').filter(x => x !== '').map(Number)
      .filter(x => !this.dataFiles[row].automaticaly_selected_stations.includes(x));

    // Trouve les stations associées au nouveau genre sélectionné
    const stationsFromGenre = this.stationWithGenresAssociation
      .filter(s => s.genres.some(g => g.toLowerCase() === newGenre.toLowerCase()))
      .map(s => s.istation);

    // Garde uniquement les nouvelles stations qui n'étaient pas déjà sélectionnées manuellement
    const valeursUniques = stationsFromGenre.filter(x => !currentStations.includes(x));

    // Met à jour la liste des stations ajoutées automatiquement
    this.dataFiles[row].automaticaly_selected_stations = valeursUniques;

    // Combine les stations sélectionnées manuellement avec celles ajoutées automatiquement
    this.dataFiles[row].music.stations = [
      ...new Set([...currentStations, ...this.dataFiles[row].automaticaly_selected_stations])
    ].join(',');

    this.hotRegister.getInstance(this.hotId).render();
  }

  updateStationsFromNewProperty(row: number, isNouvo: boolean) {
    // music.stations peut être null si la cellule a été vidée, pas juste une chaîne vide
    let currentStations = (this.dataFiles[row].music.stations || '').split(',').filter(x => x !== '').map(Number);

    if (isNouvo) {
      // Si Nouveauté est sélectionné et SUN Nouvo n'est pas déjà présent
      if (! currentStations.includes(10)) {
        currentStations.push(10);
        // On marque que l'ajout est automatique
        this.dataFiles[row].automaticaly_selected_nouvo = true;
      }
    } else {
      // Si Nouveauté est retiré et que SUN Nouvo avait été ajouté automatiquement
      if (currentStations.includes(10) && this.dataFiles[row].automaticaly_selected_nouvo === true) {
        currentStations = currentStations.filter(x => x !== 10);
        // On réinitialise le flag puisque la station a été retirée automatiquement
        this.dataFiles[row].automaticaly_selected_nouvo = false;
      }
    }

    this.dataFiles[row].music.stations = [
      ...new Set([...currentStations])
    ].join(',');

    this.hotRegister.getInstance(this.hotId).render();
  }

  waveform(pos) {
    const bottomSheetRef = this.bottomSheet.open(WaveformComponent, {
      data: {
        position: pos
      }
    });

    bottomSheetRef.afterDismissed().toPromise()
      .then((res) => {
        this.dataPassService.selectedWaveformSource.next(9999);
        if (res && res.change) {
          if (res.direction === 'next' && (pos + 1) < this.dataFiles.length) {
            this.waveform(pos + 1);
          } else if (res.direction === 'prev' && (pos - 1) >= 0) {
            this.waveform(pos - 1);
          }
        }
      });
  }

  async sendValidatedFiles() {
    const sendingData = [];
    for (const row of this.dataFiles) {
      if (row.status.general === 1 && this.checkValidatedFile(row)) {
        sendingData.push(row);
        row.status.general = 3;
      }
    }
    this.hotRegister.getInstance(this.hotId).render();
    for (const dt of sendingData) {
      await this.sendValidatedFile(dt, this.dataPassService.musiques_mix[dt.position], dt.position)
        .then(res => console.log(res))
        .catch(err => console.log(err));
    }
  }

  checkValidatedFile(row) {
    const isNew = row.music.proprietes ? row.music.proprietes.split(',').map(Number).includes(this.proprietes.find(x => x.label === 'Nouveauté').id) : false;
    return row.music.cover && row.music.cover !== ''
      && row.music.title && row.music.title !== ''
      && row.music.performer && row.music.performer !== ''
      && row.music.year && row.music.year !== ''
      && row.music.album && row.music.album !== ''
      && row.music.vef && row.music.vef !== ''
      && row.music.diffusion && row.music.diffusion !== ''
      && row.music.genre1 && row.music.genre1 !== ''
      && row.music.sexe && row.music.sexe !== ''
      && (!row.status.present_winmedia || row.status.bypass_winmedia)
      && (!isNew || (row.music.release_date && /^\d{4}-\d{2}-\d{2}$/.test(row.music.release_date)));
  }

  sendValidatedFile(dataFile, dataMix, pos) {
    console.log(dataFile);
    console.log(dataMix);
    console.log(pos);
    return new Promise((resolve, reject) => {
      this.dataFiles[pos].status.general = 4;
      this.hotRegister.getInstance(this.hotId).render();

      let flag = '';
      if (this.dataFiles[pos].music.flag && this.dataFiles[pos].music.flag !== '') {
        flag = this.countries[this.dataFiles[pos].music.flag].label;
      }

      // Convertir Française/Etrangère en VF/VE pour l'envoi
      const dataFileToSend = { ...dataFile };
      if (dataFileToSend.music) {
        dataFileToSend.music = { ...dataFileToSend.music };
        if (dataFileToSend.music.vef === 'Française') {
          dataFileToSend.music.vef = 'VF';
        } else if (dataFileToSend.music.vef === 'Etrangère') {
          dataFileToSend.music.vef = 'VE';
        }
      }

      const user = this.authentificationService.userSource.getValue();

      this.musicsDataService.sendValidatedFiles(dataFileToSend, dataMix, flag, this.proprietes, this.winmediaData.stations, user)
        .then(res => {
          this.dataFiles[pos].status.general = 5;
          this.hotRegister.getInstance(this.hotId).render();
          this.syncData();
          resolve(res);
        })
        .catch(err => {
          this.dataFiles[pos].status.general = 6;
          this.hotRegister.getInstance(this.hotId).render();
          this.syncData();
          reject(err);
        });
    });
  }

  syncData() {
    // Convertir Française/Etrangère en VF/VE pour la synchronisation
    const dataFilesToSync = this.dataFiles.map(dataFile => {
      const converted = { ...dataFile };
      if (converted.music) {
        converted.music = { ...converted.music };
        if (converted.music.vef === 'Française') {
          converted.music.vef = 'VF';
        } else if (converted.music.vef === 'Etrangère') {
          converted.music.vef = 'VE';
        }
      }
      return converted;
    });

    this.mysqlSyncService.syncImport(dataFilesToSync, this.dataPassService.musiques_mix)
      .then(res => {
        this.needSync = false;
        console.log(res);
      })
      .catch(err => console.log(err));
  }

  startInterval() {
    this.intervalSync = setInterval(() => {
      if (this.needSync === true) {
        this.syncData();
      }
    }, 15000);
  }

  stopInterval() {
    clearInterval(this.intervalSync);
  }

}
